import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { BaseComponent } from '@shared-features/components/base-component/base.component';
import { Lookup } from '@shared-features/models/Lookup.model';
import { SectionState } from '@shared-features/enums/layout.enum';
import { RoutesUtil } from '@shared-features/utils/routes.util';
import { SearchCategory } from '@shared-features/enums/shared-enums.enum';
import { ConfigConstant } from '@shared/constants';
import { BehaviorSubject, combineLatest, fromEvent, timer } from 'rxjs';
import { debounceTime, filter, tap, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchComponent extends BaseComponent {

  @ViewChild('scrollElement') scrollElementRef: ElementRef<HTMLElement>;
  @ViewChild('loadMoreTrigger') loadMoreTriggerElementRef: ElementRef<HTMLElement>;

  searchBarActive = false;
  SearchCategory = SearchCategory;
  selectedCategory: SearchCategory;
  showSearchOptions = true;
  showSearchResult = false;
  searchControl = new FormControl('');
  entities: Lookup[] = [];
  limit = 10;
  margin = 20;
  loading = false;
  endOfResult = false;
  private pagination$: BehaviorSubject<{ offset: number }>;
  clearOffset = false;
  SectionStateStatus = SectionState;
  searchToogle = false;
  closeSearch = false;

  constructor( private router: Router, private changeDetector:ChangeDetectorRef, private elementRef: ElementRef<HTMLElement>) {
    super();
    this.showSearchOptions = true;
    this.showSearchResult = false;
  }

  ngOnInit(): void {
    this.pagination$ = new BehaviorSubject({ offset: 0 });

    combineLatest([this.pagination$])
    .pipe(
      filter(() => !this.endOfResult),
      tap(() => this.loading = true),
      debounceTime(500),
      takeUntil(this.destroy$)
    )
    .subscribe((data) => {
      this.offset = data[0].offset | ConfigConstant.INIT_PAGE_OFFSET;
    });

    this.searchControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.showSearchOptions = true;
      this.showSearchResult = false;
    });
  }

  @HostListener('document:click', ['$event.target'])
  onDocumentClick(target: HTMLElement): void {
    if (!this.elementRef.nativeElement.contains(target)) {
      this.showSearchOptions = false;
      this.showSearchResult = false;
      this.changeDetector.markForCheck();
    }
  }

  ngAfterViewInit(): void {
    this.handleScrollEvent();
  }

  searchByCategory(value) {
    this.showSearchOptions = false;
    this.selectedCategory = value;
    this.offset = ConfigConstant.INIT_PAGE_OFFSET;
    this.endOfResult = false;
    this.entities = [];
  }

  navigateToDetailsPage(id) {
    switch (this.selectedCategory) {
      case SearchCategory.Driver:
        this.router.navigate([RoutesUtil.FleetsDriversView.url({ id })]);
        break;

      case SearchCategory.Truck:
        this.router.navigate([RoutesUtil.FleetsTrucksView.url({ id })]);
        break;
    }

    this.clear();
  }

    // private methods
    handleScrollEvent(): void {
      const $scrollElement = this.scrollElementRef?.nativeElement;
      if (!$scrollElement) {
        return;
      }
      fromEvent($scrollElement, 'scroll')
        .pipe(
          takeUntil(this.destroy$),
          debounceTime(500),
          filter(() => !this.loading && !this.endOfResult),
          filter(() => this.isLoadMoreTriggerVisible())
        )
        .subscribe(() => {
          this.loadMore();
        });
    }

    private isLoadMoreTriggerVisible(): boolean {
      const $scrollElement = this.scrollElementRef?.nativeElement;
      const $triggerElement = this.loadMoreTriggerElementRef?.nativeElement;

      if (!$scrollElement || !$triggerElement) {
        return false;
      }

      const { height } = $scrollElement.getBoundingClientRect();
      const { top } = $triggerElement.getBoundingClientRect();
      const scroll = $scrollElement.scrollTop;

      if (top === 0 && height === 0) {
        return false;
      }
      return top <= height + scroll + this.margin;
    }

    loadMore(): void {
      this.pagination$.next({ offset: this.offset + 1  });
    }

    clear() {
      this.endOfResult = false;
      this.offset = ConfigConstant.INIT_PAGE_OFFSET;
      this.showSearchOptions = false;
      this.showSearchResult = false;
      this.entities = [];
      this.searchControl.setValue(null);
      this.closeSearch = !this.closeSearch
      timer(500).subscribe(()=>{
        this.closeSearch = !this.closeSearch
        this.searchToogle = !this.searchToogle
        this.changeDetector.markForCheck()
      })
    }

    resize$ = fromEvent(window, 'resize').pipe(debounceTime(250), takeUntil(this.destroy$)).subscribe(() => {
      this.showSearchOptions = false;
      this.showSearchResult = false;
    });
}
